/**
 * Styled components for CartSheet (mobile bottom sheet).
 *
 * Self-check:
 * - Mobile-only: SheetBackdrop and SheetPanel hidden on desktop via @media (min-width: 769px) { display: none; }
 * - z-index: SheetBackdrop=1299, SheetPanel=1300 (same stack as CartDrawer — mutually exclusive by viewport)
 * - DragHandleRow has touch-action: none (RESEARCH.md Pitfall 7 — iOS Safari scroll vs drag conflict)
 * - DragHandle: 40px wide, 4px tall pill (D-07, UI-SPEC)
 * - No backdrop dimming (D-02)
 * - All tokens from theme (no raw colors)
 */

import styled from "styled-components";
import { Button } from "../../ui/Button";

// ── SheetBackdrop ────────────────────────────────────────────────────────────
// Transparent fixed overlay present while sheet is open.
// D-06: no backdrop tap dismiss — close via swipe-down or X button only.
// Hidden on desktop.
export const SheetBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1299;
  background: transparent;

  @media (min-width: 769px) {
    display: none;
  }
`;

// ── SheetPanel ───────────────────────────────────────────────────────────────
// The visible bottom sheet container. Rises from the bottom edge.
// Hidden on desktop (CartDrawer handles that viewport).
export const SheetPanel = styled.div`
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(40px) saturate(180%) brightness(1.05);
  -webkit-backdrop-filter: blur(40px) saturate(180%) brightness(1.05);
  border-radius: ${({ theme }) => theme.radius.xl}px
    ${({ theme }) => theme.radius.xl}px 0 0;
  border-top: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  box-shadow: ${({ theme }) => theme.colors.background.liquidGlassHighlight},
    0 -4px 24px ${({ theme }) => theme.colors.shadow.high};
  display: flex;
  flex-direction: column;
  width: 100%;
  height: min(82vh, calc(100vh - 12px));
  max-height: calc(100vh - 12px);
  overflow: hidden;
  will-change: transform;

  @media (min-width: 769px) {
    display: none;
  }
`;

// ── DragHandleRow ────────────────────────────────────────────────────────────
// Touch target row for the drag handle — initiates framer-motion drag.
// touch-action: none prevents iOS Safari from intercepting pointer events
// before framer-motion can capture them (RESEARCH.md Pitfall 7).
export const DragHandleRow = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing(1.5)} 0`};
  touch-action: none;
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`;

// ── DragHandle ───────────────────────────────────────────────────────────────
// Visible pill indicator (D-07): 40px wide, 4px tall.
export const DragHandle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: ${({ theme }) => theme.radius.pill}px;
  background: ${({ theme }) => theme.colors.border.default};
`;

// ── SheetHeader ──────────────────────────────────────────────────────────────
export const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(2)}`};
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  flex-shrink: 0;
  touch-action: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

// ── SheetTitle ───────────────────────────────────────────────────────────────
export const SheetTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.h3};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

// ── SheetCloseButton ─────────────────────────────────────────────────────────
export const SheetCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.pill}px;
  width: ${({ theme }) => theme.spacing(4.5)};
  height: ${({ theme }) => theme.spacing(4.5)};
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.background.glass};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }
`;

// ── SheetItemList ────────────────────────────────────────────────────────────
export const SheetItemList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
`;

// ── Item sub-components (duplicated from CartDrawer.styled.tsx for clean import) ──
// Keeping these local avoids circular dependencies and keeps each styled file
// fully self-contained (each file maps 1:1 to its component).

export const ItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  position: relative;
`;

export const ItemThumb = styled.img`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  object-fit: cover;
  flex-shrink: 0;
`;

export const ItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
  min-width: 0;
`;

export const ItemName = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
`;

export const ItemPrice = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  flex-shrink: 0;
`;

export const QtyControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const QtyBtn = styled.button`
  width: ${({ theme }) => theme.spacing(6)};
  height: ${({ theme }) => theme.spacing(6)};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 600;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.state.hover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.text.disabled};
    border-color: ${({ theme }) => theme.colors.border.subtle};
    background: ${({ theme }) => theme.colors.background.surface};
  }
`;

export const QtyValue = styled.span`
  min-width: ${({ theme }) => theme.spacing(2.5)};
  text-align: center;
  font-weight: 600;
  font-size: ${({ theme }) => theme.typography.small};
`;

export const RemoveBtn = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.accent.danger};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing(0.5)};
  min-width: ${({ theme }) => theme.spacing(6)};
  min-height: ${({ theme }) => theme.spacing(6)};
  position: absolute;
  top: 0;
  right: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.sm}px;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    background: ${({ theme }) => theme.colors.accent.danger};
    color: ${({ theme }) => theme.colors.text.inverse};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.destructive};
  }
`;

// ── SheetFooter ──────────────────────────────────────────────────────────────
export const SheetFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(2)}`};
  padding-bottom: ${({ theme }) =>
    `calc(${theme.spacing(3)} + env(safe-area-inset-bottom, 0px))`};
  flex-shrink: 0;
`;

export const SubtotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

export const SubtotalLabel = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const SubtotalValue = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const SubtotalNote = styled.p`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
`;

// ── SheetEmptyWrap ───────────────────────────────────────────────────────────
export const SheetEmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(4)};
  text-align: center;
`;

export const SheetEmptyHeading = styled.h3`
  font-size: ${({ theme }) => theme.typography.h3};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const SheetEmptyBody = styled.p`
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  text-align: center;
`;

// ── SheetCheckoutBtn ─────────────────────────────────────────────────────────
export const SheetCheckoutBtn = styled(Button)`
  width: 100%;
  min-height: ${({ theme }) => theme.spacing(6)};
`;

// ── RecsSection ──────────────────────────────────────────────────────────────
export const RecsSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
  padding: ${({ theme }) => theme.spacing(2)};
  flex-shrink: 0;
`;

export const RecsHeading = styled.p`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 ${({ theme }) => theme.spacing(1.5)} 0;
`;

export const RecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const RecsSkeleton = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: ${({ theme }) => theme.radius.md}px;
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
`;

export const RecCard = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: ${({ theme }) => theme.radius.md}px;
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  overflow: hidden;
  cursor: pointer;
  transition: transform ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }
`;

export const RecThumb = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
`;

export const RecInfo = styled.div`
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1)}`};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
  flex: 1;
`;

export const RecName = styled.p`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const RecPrice = styled.p`
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const RecAddBtn = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(0.75)};
  background: ${({ theme }) => theme.colors.accent.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    opacity: 0.92;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: -2px;
  }
`;
